import { ChangeEvent } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { getMediaFilesAPI } from '@myme/api';
import { addMediaFile } from '@myme/logic';
import {
  allAvailableFormats,
  availableAudioFormats,
  availableImageFormats,
  availableVideoFormats,
  MediaBlobType
} from '@myme/utils';
import { useTranslation } from 'src/hooks';
import { toast, ToastType } from 'src/utils';

const getAvailableExtensionsBasedOnMediaType = (mediaType: MediaBlobType | 'ALL') => {
  switch (mediaType) {
    case 'IMAGE': {
      return availableImageFormats;
    }
    case 'VIDEO': {
      return availableVideoFormats;
    }
    case 'AUDIO': {
      return availableAudioFormats;
    }
    case 'ALL':
    default: {
      return allAvailableFormats;
    }
  }
};

export const useUploadNewMediaFile = (type: MediaBlobType | 'ALL') => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const handleUploadNewMediaFileMutation = useMutation(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const extensions = getAvailableExtensionsBasedOnMediaType(type).split(',');

      const formData = new FormData();
      if (e.target.files?.length === 1) {
        const fileName = e.target.files[0].name;
        const lastDot = fileName.lastIndexOf('.');
        const fileExtension = `.${fileName.substring(lastDot + 1)}`;
        const isValidExtension = extensions.some(ext => ext === fileExtension);

        if (!isValidExtension) {
          throw new Error('You cannot upload file with such extension');
        }

        formData.append('file', e.target.files[0]);
      }
      await addMediaFile(formData);
    },
    {
      onSuccess: () => {
        toast(ToastType.SUCCESS, t('pages.mymes.editor.blobs.library.media_upload_toast_success'));
        queryClient.refetchQueries(getMediaFilesAPI.cacheKeys);
      },
      onError: () => {
        toast(ToastType.ERROR, 'You cannot upload file with such extension');
      }
    }
  );

  return { handleUploadNewMediaFileMutation };
};
